<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'ExpenseCategory',
  },
})

const { useAbility } = await import('@casl/vue')
const ability = useAbility()

const { useApi } = await import('@/utils/api')

// ── State ────────────────────────────────────────────────────────────────────
const categories = ref([])
const loading = ref(false)
const search = ref('')
const activeOnly = ref(false)

// Dialog state
const dialog = ref(false)
const dialogTitle = ref('')
const editingId = ref(null)
const saving = ref(false)
const errors = ref({})

// Delete dialog
const deleteDialog = ref(false)
const deletingId = ref(null)
const deleting = ref(false)

// Form
const form = ref({
  name: '',
  color: '#6366F1',
  is_active: true,
})

// ── Fetch ────────────────────────────────────────────────────────────────────
const fetchCategories = async () => {
  loading.value = true
  try {
    const response = await useApi('/api/v1/expense-categories', {
      query: { active_only: activeOnly.value ? 1 : 0 },
    }).get()
    const data = response?.data ?? response
    categories.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

onMounted(fetchCategories)
watch(activeOnly, fetchCategories)

// ── Computed ─────────────────────────────────────────────────────────────────
const filteredCategories = computed(() => {
  if (!search.value) return categories.value
  const q = search.value.toLowerCase()
  return categories.value.filter(c => c.name.toLowerCase().includes(q))
})

// ── Dialog ───────────────────────────────────────────────────────────────────
const openCreate = () => {
  editingId.value = null
  form.value = { name: '', color: '#6366F1', is_active: true }
  errors.value = {}
  dialogTitle.value = 'Tambah Kategori'
  dialog.value = true
}

const openEdit = (cat) => {
  editingId.value = cat.id
  form.value = { name: cat.name, color: cat.color ?? '#6366F1', is_active: cat.is_active }
  errors.value = {}
  dialogTitle.value = 'Edit Kategori'
  dialog.value = true
}

const closeDialog = () => {
  dialog.value = false
}

const save = async () => {
  saving.value = true
  errors.value = {}
  try {
    const api = useApi(
      editingId.value
        ? `/api/v1/expense-categories/${editingId.value}`
        : '/api/v1/expense-categories',
    )
    const response = editingId.value
      ? await api.put({ body: form.value })
      : await api.post({ body: form.value })

    const data = response?.data ?? response
    if (editingId.value) {
      const idx = categories.value.findIndex(c => c.id === editingId.value)
      if (idx !== -1) categories.value[idx] = data
    } else {
      categories.value.unshift(data)
    }
    dialog.value = false
  } catch (e) {
    if (e?.response?.status === 422) {
      errors.value = e?.data?.errors ?? {}
    } else {
      console.error(e)
    }
  } finally {
    saving.value = false
  }
}

// ── Delete ───────────────────────────────────────────────────────────────────
const confirmDelete = (id) => {
  deletingId.value = id
  deleteDialog.value = true
}

const doDelete = async () => {
  deleting.value = true
  try {
    await useApi(`/api/v1/expense-categories/${deletingId.value}`).delete()
    categories.value = categories.value.filter(c => c.id !== deletingId.value)
    deleteDialog.value = false
  } catch (e) {
    console.error(e)
  } finally {
    deleting.value = false
  }
}

// ── Color swatch ─────────────────────────────────────────────────────────────
const PRESET_COLORS = [
  '#6366F1', '#8B5CF6', '#EC4899', '#EF4444',
  '#F97316', '#EAB308', '#22C55E', '#14B8A6',
  '#3B82F6', '#6B7280',
]
</script>

<template>
  <div>
    <!-- Header -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">Kategori Pengeluaran</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Kelola kategori untuk mencatat pengeluaran operasional
            </p>
          </div>
          <VBtn
            v-if="ability.can('create', 'ExpenseCategory')"
            prepend-icon="ri-add-line"
            @click="openCreate"
          >
            Tambah Kategori
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <!-- Filters -->
    <VCard class="mb-4">
      <VCardText>
        <VRow>
          <VCol cols="12" md="6">
            <VTextField
              v-model="search"
              placeholder="Cari nama kategori..."
              prepend-inner-icon="ri-search-line"
              clearable
              hide-details
              density="compact"
            />
          </VCol>
          <VCol cols="12" md="6" class="d-flex align-center">
            <VSwitch
              v-model="activeOnly"
              label="Tampilkan aktif saja"
              color="primary"
              hide-details
              density="compact"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Table -->
    <VCard>
      <VCardText v-if="loading" class="text-center py-8">
        <VProgressCircular indeterminate color="primary" />
      </VCardText>

      <template v-else-if="filteredCategories.length === 0">
        <VCardText class="text-center py-12">
          <VIcon icon="ri-inbox-line" size="64" color="secondary" class="mb-4" />
          <p class="text-h6 text-medium-emphasis">Belum ada kategori</p>
          <p class="text-body-2 text-medium-emphasis">
            Klik "Tambah Kategori" untuk mulai menambahkan
          </p>
        </VCardText>
      </template>

      <VTable v-else hover>
        <thead>
          <tr>
            <th>Nama</th>
            <th>Warna</th>
            <th>Status</th>
            <th class="text-center">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="cat in filteredCategories" :key="cat.id">
            <td class="font-weight-medium">{{ cat.name }}</td>
            <td>
              <div class="d-flex align-center gap-2">
                <div
                  :style="`background:${cat.color ?? '#6366F1'};width:20px;height:20px;border-radius:4px;`"
                />
                <span class="text-body-2 text-medium-emphasis">{{ cat.color }}</span>
              </div>
            </td>
            <td>
              <VChip
                :color="cat.is_active ? 'success' : 'secondary'"
                size="small"
                label
              >
                {{ cat.is_active ? 'Aktif' : 'Nonaktif' }}
              </VChip>
            </td>
            <td class="text-center">
              <VBtn
                v-if="ability.can('update', 'ExpenseCategory')"
                icon="ri-pencil-line"
                variant="text"
                size="small"
                color="primary"
                @click="openEdit(cat)"
              />
              <VBtn
                v-if="ability.can('delete', 'ExpenseCategory')"
                icon="ri-delete-bin-line"
                variant="text"
                size="small"
                color="error"
                @click="confirmDelete(cat.id)"
              />
            </td>
          </tr>
        </tbody>
      </VTable>
    </VCard>

    <!-- Create/Edit Dialog -->
    <VDialog v-model="dialog" max-width="480" persistent>
      <VCard>
        <VCardTitle class="pa-6 pb-0">{{ dialogTitle }}</VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                label="Nama Kategori"
                placeholder="cth. Bahan Baku, Listrik, Gaji"
                :error-messages="errors.name"
                autofocus
              />
            </VCol>
            <VCol cols="12">
              <p class="text-body-2 mb-2">Warna</p>
              <div class="d-flex flex-wrap gap-2 mb-3">
                <div
                  v-for="c in PRESET_COLORS"
                  :key="c"
                  :style="`background:${c};width:28px;height:28px;border-radius:6px;cursor:pointer;outline:${form.color === c ? '3px solid currentColor' : 'none'};outline-offset:2px;`"
                  @click="form.color = c"
                />
              </div>
              <VTextField
                v-model="form.color"
                label="Kode Warna (Hex)"
                placeholder="#6366F1"
                :error-messages="errors.color"
                density="compact"
              >
                <template #prepend-inner>
                  <div
                    :style="`background:${form.color};width:16px;height:16px;border-radius:3px;`"
                  />
                </template>
              </VTextField>
            </VCol>
            <VCol cols="12">
              <VSwitch
                v-model="form.is_active"
                label="Kategori Aktif"
                color="success"
                hide-details
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn variant="tonal" color="secondary" :disabled="saving" @click="closeDialog">
            Batal
          </VBtn>
          <VBtn color="primary" :loading="saving" @click="save">
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Delete Confirm Dialog -->
    <VDialog v-model="deleteDialog" max-width="400">
      <VCard>
        <VCardTitle class="pa-6 pb-2">Hapus Kategori?</VCardTitle>
        <VCardText class="pa-6 pt-0">
          Kategori yang sudah memiliki data pengeluaran tidak dapat dihapus.
          Pastikan kategori ini belum dipakai.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn variant="tonal" color="secondary" :disabled="deleting" @click="deleteDialog = false">
            Batal
          </VBtn>
          <VBtn color="error" :loading="deleting" @click="doDelete">
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
